import type { SignalDto } from '@nova/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, query } from '../../lib/api.js';

/** Signals from the scout (Omverdensovervåkeren) — newest and most relevant first. */
export type SignalSort = 'newest' | 'relevance';

export function useSignals(params?: {
  take?: number;
  status?: SignalDto['status'];
  /** Only signals for this product area; omitted = all areas. */
  areaId?: string;
  /** Sort order: newest received first (default) or most relevant first. */
  sort?: SignalSort;
}) {
  const status = params?.status ?? 'NEW';
  const take = params?.take ?? 12;
  const areaId = params?.areaId || undefined;
  const sort = params?.sort ?? 'newest';
  return useQuery({
    queryKey: ['signals', { status, take, areaId, sort }],
    queryFn: () =>
      api.get<SignalDto[]>(
        `/signals${query({ status, take, areaId, sort, excludeSeed: undefined })}`,
      ),
  });
}

/**
 * Turns a signal into a proposal card (SUGGESTED) and marks the signal used. The
 * human then reviews and reworks the card — this is the signal → forslag step.
 */
export function useCreateFromSignal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (signalId: string) =>
      api.post<{ id: string }>(`/signals/${signalId}/to-content`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['signals'] });
      void queryClient.invalidateQueries({ queryKey: ['content'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}
